import React, { useCallback, useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type PaginationState,
  type RowSelectionState,
  type SortingState,
  type VisibilityState,
  type ColumnOrderState,
} from '@tanstack/react-table';
import { Search } from 'lucide-react';
import { Tooltip, TooltipProvider } from '../../admin/components/shared/Tooltip.tsx';
import { TableContainer } from './TableContainer.tsx';
import { TableStateRow } from './TableStateRow.tsx';
import { Table } from './Table.tsx';
import { ColumnVisibilityMenu } from './ColumnVisibilityMenu.tsx';
import { TablePaginationFooter } from './TablePaginationFooter.tsx';
import { DEFAULT_PAGE_SIZE_OPTIONS, normalizePageSize } from './pagination.ts';
import type { TableColumnDef, TableProps } from './types.ts';

function makeDefaultPagination(pageSize: number): PaginationState {
  return { pageIndex: 0, pageSize };
}

function renderTextWithOverflowRules(
  value: string | number,
  overflowBehavior: 'truncate' | 'wrap',
  overflowLimit: number,
  textBold = false,
  showTooltip = true
): React.ReactElement {
  const text = String(value);
  const textClassName = textBold ? 'font-medium' : '';
  const title = showTooltip ? text : undefined;
  if (overflowBehavior === 'wrap') {
    return (
      <span title={title} className={`whitespace-normal break-words ${textClassName}`.trim()}>
        {text}
      </span>
    );
  }
  const shouldClipByChars = text.length > overflowLimit;
  const displayText = shouldClipByChars ? `${text.slice(0, overflowLimit)}...` : text;
  return (
    <span
      title={title}
      className={`block w-full min-w-0 truncate ${textClassName}`.trim()}
    >
      {displayText}
    </span>
  );
}

function extractNodeText(node: ReactNode): string {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map((n) => extractNodeText(n)).join('');
  if (React.isValidElement(node)) {
    const element = node as React.ReactElement<{ children?: ReactNode }>;
    return extractNodeText(element.props?.children);
  }
  return '';
}

export function AppDataTable<TData>({
  data,
  columns,
  mode = 'client',
  searchable = false,
  searchPlaceholder = 'Search...',
  headerStart,
  showColumnVisibilityMenu,
  enableColumnReorder = false,
  columnVisibilityMenuAriaLabel,
  topToolbar,
  bulkActionBar,
  loading = false,
  error = null,
  emptyMessage = 'No data available',
  paginated = false,
  defaultPageSize = 10,
  showPagination = true,
  paginationItemLabel = 'results',
  showPageSizeSelector = false,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  pageSizeSelectorLabel = 'Rows per page',
  sorting: controlledSorting,
  pagination: controlledPagination,
  globalFilter: controlledGlobalFilter,
  columnVisibility: controlledColumnVisibility,
  columnOrder: controlledColumnOrder,
  onSortingChange,
  onPaginationChange,
  onGlobalFilterChange,
  onColumnVisibilityChange,
  onColumnOrderChange,
  rowCount,
  pageCount,
  className,
  onRowClick,
  isRowSelected,
  showCellTooltips = false,
  getRowId,
  defaultColumnVisibility,
  allowColumnOverflowScroll = false,
}: TableProps<TData>): React.ReactElement {
  const [internalSorting, setInternalSorting] = useState<SortingState>([]);
  const [internalPagination, setInternalPagination] = useState<PaginationState>(() =>
    makeDefaultPagination(defaultPageSize)
  );
  const [internalGlobalFilter, setInternalGlobalFilter] = useState('');
  const [internalColumnVisibility, setInternalColumnVisibility] = useState<VisibilityState>(
    () => defaultColumnVisibility ?? {}
  );
  const [internalColumnOrder, setInternalColumnOrder] = useState<ColumnOrderState>(
    () => controlledColumnOrder ?? []
  );
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const sorting = controlledSorting ?? internalSorting;
  const pagination = controlledPagination ?? internalPagination;
  const globalFilter = controlledGlobalFilter ?? internalGlobalFilter;
  const columnVisibility = controlledColumnVisibility ?? internalColumnVisibility;
  const columnOrder = controlledColumnOrder ?? internalColumnOrder;
  const resetColumnVisibilityState = defaultColumnVisibility ?? {};


  useEffect(() => {
    if (!paginated || controlledPagination != null) return;
    setInternalPagination((prev) => (prev.pageSize === defaultPageSize ? prev : { ...prev, pageSize: defaultPageSize }));
  }, [paginated, controlledPagination, defaultPageSize]);

  const resolvedColumns = useMemo((): ColumnDef<TData, unknown>[] => {
    return columns.map((col) => {
      const def = col as TableColumnDef<TData>;
      if (def.hideable === false) {
        return { ...col, enableHiding: false };
      }
      if (def.hideable === true && col.enableHiding === undefined) {
        return { ...col, enableHiding: true };
      }
      return col;
    });
  }, [columns]);

  const isServerMode = mode === 'server';

  const tableState = useMemo(() => {
    const base = { sorting, globalFilter, columnVisibility, rowSelection, columnOrder };
    if (paginated || isServerMode) {
      return { ...base, pagination };
    }
    return base;
  }, [paginated, isServerMode, sorting, pagination, globalFilter, columnVisibility, rowSelection, columnOrder]);

  const effectivePageCount = pageCount ?? (rowCount ? Math.max(1, Math.ceil(rowCount / pagination.pageSize)) : undefined);

  const applyColumnOrder = useCallback(
    (next: ColumnOrderState) => {
      onColumnOrderChange?.(next);
      if (!controlledColumnOrder) setInternalColumnOrder(next);
    },
    [controlledColumnOrder, onColumnOrderChange]
  );

  const table = useReactTable({
    data,
    columns: resolvedColumns,
    state: tableState,
    initialState: {
      columnVisibility: resetColumnVisibilityState,
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: isServerMode ? undefined : getFilteredRowModel(),
    getSortedRowModel: isServerMode ? undefined : getSortedRowModel(),
    getPaginationRowModel: paginated && !isServerMode ? getPaginationRowModel() : undefined,
    onSortingChange: (updater) => {
      const next = typeof updater === 'function' ? updater(sorting) : updater;
      onSortingChange?.(next);
      if (!controlledSorting) setInternalSorting(next);
    },
    onPaginationChange:
      paginated || isServerMode
        ? (updater) => {
            const nextRaw = typeof updater === 'function' ? updater(pagination) : updater;
            const pageSizeChanged = nextRaw.pageSize !== pagination.pageSize;
            const next = {
              ...nextRaw,
              pageSize: normalizePageSize(nextRaw.pageSize, pageSizeOptions),
              pageIndex: pageSizeChanged ? 0 : nextRaw.pageIndex,
            };
            onPaginationChange?.(next);
            if (!controlledPagination) setInternalPagination(next);
          }
        : undefined,
    onGlobalFilterChange: (updater) => {
      const next = typeof updater === 'function' ? updater(globalFilter) : updater;
      onGlobalFilterChange?.(next);
      if (!controlledGlobalFilter) setInternalGlobalFilter(next);
    },
    onColumnVisibilityChange: (updater) => {
      const next = typeof updater === 'function' ? updater(columnVisibility) : updater;
      onColumnVisibilityChange?.(next);
      if (!controlledColumnVisibility) setInternalColumnVisibility(next);
    },
    onColumnOrderChange: (updater) => {
      const next = typeof updater === 'function' ? updater(columnOrder) : updater;
      applyColumnOrder(next);
    },
    onRowSelectionChange: setRowSelection,
    manualSorting: isServerMode,
    manualFiltering: isServerMode,
    manualPagination: isServerMode,
    pageCount: isServerMode ? effectivePageCount : undefined,
    rowCount: isServerMode ? rowCount : undefined,
    getRowId,
  });

  const hideableLeafColumns = useMemo(() => {
    if (showColumnVisibilityMenu !== true) return [];
    const hideable = table.getAllLeafColumns().filter((col) => col.getCanHide());
    // TanStack getAllLeafColumns() keeps definition order — not columnOrder.
    // Sort the gear list by columnOrder so the menu stays in sync with the table.
    if (!enableColumnReorder) return hideable;
    const byId = new Map(hideable.map((col) => [col.id, col]));
    const ordered = [];
    const preferred =
      columnOrder.length > 0 ? columnOrder : hideable.map((col) => col.id);
    for (const id of preferred) {
      const col = byId.get(id);
      if (!col) continue;
      ordered.push(col);
      byId.delete(id);
    }
    for (const col of byId.values()) ordered.push(col);
    return ordered;
  }, [table, showColumnVisibilityMenu, enableColumnReorder, columnOrder]);

  const shouldShowColumnMenu = showColumnVisibilityMenu === true && hideableLeafColumns.length > 0;

  const handleMenuColumnReorder = useCallback(
    (orderedHideableIds: string[]) => {
      if (!enableColumnReorder) return;
      const allLeafIds = table.getAllLeafColumns().map((col) => col.id);
      const currentOrder =
        columnOrder.length > 0
          ? (() => {
              // Reconcile: keep known order, append any new leaf ids.
              const known = new Set(allLeafIds);
              const filtered = columnOrder.filter((id) => known.has(id));
              for (const id of allLeafIds) {
                if (!filtered.includes(id)) filtered.push(id);
              }
              return filtered;
            })()
          : allLeafIds;

      const hideableSet = new Set(orderedHideableIds);
      let hideableCursor = 0;
      const next = currentOrder.map((id) => {
        if (!hideableSet.has(id)) return id;
        const replacement = orderedHideableIds[hideableCursor];
        hideableCursor += 1;
        return replacement ?? id;
      });
      // Append any hideable ids that somehow weren't in currentOrder.
      for (const id of orderedHideableIds) {
        if (!next.includes(id)) next.push(id);
      }
      applyColumnOrder(next);
    },
    [applyColumnOrder, columnOrder, enableColumnReorder, table]
  );

  const handleColumnMenuReset = useCallback(() => {
    table.resetColumnVisibility();
    if (enableColumnReorder) {
      applyColumnOrder([]);
    }
  }, [applyColumnOrder, enableColumnReorder, table]);

  const visibleColumnCount = useMemo(
    () => table.getVisibleLeafColumns().length || resolvedColumns.length,
    [table, resolvedColumns.length]
  );

  const columnWidthStyleById = useMemo(() => {
    const widthEntries = table
      .getVisibleLeafColumns()
      .map((column) => {
        const widthPercent = (column.columnDef as TableColumnDef<TData>).widthPercent;
        return typeof widthPercent === 'number' && Number.isFinite(widthPercent) && widthPercent > 0
          ? [column.id, widthPercent] as const
          : null;
      })
      .filter((entry): entry is readonly [string, number] => entry != null);

    if (widthEntries.length === 0) {
      return {
        styles: new Map<string, CSSProperties>(),
        tableMinWidthPx: null as number | null,
        tableWidthPercent: null as number | null,
      };
    }

    const total = widthEntries.reduce((sum, [, width]) => sum + width, 0);

    // Overflow mode (controls + risks): expand the table past 100% of the
    // container and keep each column's absolute share of the viewport.
    // Example: a 10-wide column stays ~10% of the viewport after adding gear
    // columns, instead of being scaled down. Extra width scrolls horizontally.
    if (allowColumnOverflowScroll && total > 100) {
      const styles = new Map(
        widthEntries.map(([id, width]) => {
          const pctOfTable = (width / total) * 100;
          return [id, { width: `${pctOfTable}%` } satisfies CSSProperties];
        })
      );
      return { styles, tableMinWidthPx: null, tableWidthPercent: total };
    }

    // Exact prior Risks/dev behavior: scale percentages into the viewport.
    const scale = total > 100 ? 100 / total : 1;
    return {
      styles: new Map(
        widthEntries.map(([id, width]) => {
          const value = `${width * scale}%`;
          return [id, { width: value, minWidth: value } satisfies CSSProperties];
        })
      ),
      tableMinWidthPx: null as number | null,
      tableWidthPercent: null as number | null,
    };
  }, [table, allowColumnOverflowScroll]);

  const columnWidthStyles = columnWidthStyleById.styles;
  const tableMinWidthPx = columnWidthStyleById.tableMinWidthPx;
  const tableWidthPercent = columnWidthStyleById.tableWidthPercent;

  const rows = table.getRowModel().rows;
  const showStateRow = loading || !!error || rows.length === 0;

  const totalRows = isServerMode ? (rowCount ?? rows.length) : table.getFilteredRowModel().rows.length;
  const from =
    totalRows === 0 || !paginated ? 0 : pagination.pageIndex * pagination.pageSize + 1;
  const to =
    !paginated || totalRows === 0
      ? totalRows
      : Math.min((pagination.pageIndex + 1) * pagination.pageSize, totalRows);

  const showHeaderBar = headerStart != null || shouldShowColumnMenu;

  return (
    <TooltipProvider>
    <TableContainer className={className}>
      {showHeaderBar ? (
        <div
          className={`px-6 py-4 border-b border-gray-200 flex items-center gap-3 ${
            headerStart != null && shouldShowColumnMenu ? 'justify-between' : 'justify-end'
          }`}
        >
          {headerStart != null ? <div className="min-w-0 flex-1">{headerStart}</div> : null}
          {shouldShowColumnMenu ? (
            <ColumnVisibilityMenu
              columns={hideableLeafColumns}
              onReset={handleColumnMenuReset}
              ariaLabel={columnVisibilityMenuAriaLabel}
              enableReorder={enableColumnReorder === true}
              onReorder={enableColumnReorder === true ? handleMenuColumnReorder : undefined}
            />
          ) : null}
        </div>
      ) : null}
      {topToolbar}
      {bulkActionBar}
      {searchable && (
        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={globalFilter ?? ''}
              onChange={(e) => table.setGlobalFilter(e.target.value)}
              placeholder={searchPlaceholder}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
      )}

      <Table
        className={`w-full ${tableWidthPercent || tableMinWidthPx ? 'table-fixed' : 'table-auto'}`}
        style={
          tableWidthPercent
            ? { width: `${tableWidthPercent}%`, minWidth: `${tableWidthPercent}%` }
            : tableMinWidthPx
              ? { minWidth: `${tableMinWidthPx}px` }
              : undefined
        }
      >
        <Table.Head>
          {table.getHeaderGroups().map((headerGroup) => (
            <Table.Row key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Table.HeaderCell
                    key={header.id}
                    style={columnWidthStyles.get(header.column.id)}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </Table.HeaderCell>
                ))}
            </Table.Row>
            ))}
        </Table.Head>
        <Table.Body>
            {showStateRow ? (
              <TableStateRow
                colSpan={visibleColumnCount}
                loading={loading}
                error={error}
                emptyMessage={emptyMessage}
              />
            ) : (
              rows.map((row) => (
                <Table.Row
                  key={row.id}
                  className={`${
                    isRowSelected?.(row.original) ? 'bg-blue-100/60 hover:bg-blue-100/80' : 'hover:bg-gray-50'
                  } ${onRowClick ? 'cursor-pointer' : ''}`}
                  onClick={() => onRowClick?.(row.original)}
                >
                  {row.getVisibleCells().map((cell) => {
                    const rendered = flexRender(cell.column.columnDef.cell, cell.getContext());
                    const columnDef = cell.column.columnDef as TableColumnDef<TData>;
                    const overflowBehavior =
                      columnDef.truncate === true
                        ? 'truncate'
                        : columnDef.textOverflowBehavior ??
                          (columnDef.truncate === false ? 'wrap' : 'truncate');
                    const overflowLimit = columnDef.truncateLimit ?? columnDef.textOverflowLimit ?? 50;
                    const showTooltip = columnDef.truncateTooltip ?? true;
                    const textBold = columnDef.textBold === true;
                    const hasTextFormattingProps =
                      columnDef.truncate !== undefined ||
                      columnDef.truncateLimit !== undefined ||
                      columnDef.textOverflowBehavior !== undefined ||
                      columnDef.textOverflowLimit !== undefined ||
                      columnDef.textBold !== undefined ||
                      columnDef.truncateTooltip !== undefined;

                    const renderedText = extractNodeText(rendered).trim();
                    const cellTitle = showCellTooltips && renderedText !== '' ? renderedText : undefined;

                    const content = (() => {
                      if (!hasTextFormattingProps) return rendered;
                      if (renderedText !== '') {
                        return renderTextWithOverflowRules(
                          renderedText,
                          overflowBehavior,
                          overflowLimit,
                          textBold,
                          showTooltip
                        );
                      }
                      return (
                        <Tooltip key={`${cell.id}-tooltip`} content={rendered} side="top">
                        <span
                          className={`block w-full min-w-0 ${
                            overflowBehavior === 'truncate' ? 'truncate' : 'whitespace-normal break-words'
                          } ${textBold ? 'font-semibold' : ''}`.trim()}
                        >
                          {rendered}
                        </span>
                        </Tooltip>
                      );
                    })();

                    return (
                      <Table.Cell key={cell.id} style={columnWidthStyles.get(cell.column.id)} title={cellTitle}>
                        {content}
                      </Table.Cell>
                    );
                  })}
                </Table.Row>
              ))
            )}
        </Table.Body>
      </Table>

      {paginated &&
        showPagination !== false &&
        totalRows > 0 &&
        !loading &&
        !error && (
          <TablePaginationFooter
            className="mt-0"
            from={from}
            to={to}
            total={totalRows}
            itemLabel={paginationItemLabel}
            pageIndex={pagination.pageIndex}
            pageCount={effectivePageCount}
            canPreviousPage={table.getCanPreviousPage()}
            canNextPage={table.getCanNextPage()}
            onPreviousPage={() => table.previousPage()}
            onNextPage={() => table.nextPage()}
            showPageSizeSelector={showPageSizeSelector}
            pageSize={pagination.pageSize}
            pageSizeOptions={pageSizeOptions}
            pageSizeSelectorLabel={pageSizeSelectorLabel}
            onPageSizeChange={(nextSize) => {
              table.setPagination({ pageIndex: 0, pageSize: nextSize });
            }}
          />
        )}
    </TableContainer>
    </TooltipProvider>
  );
}
